import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Navbar from '../components/Navbar';
import SimplePassportCard from '../components/SimplePassportCard';
import { ArrowLeft, Shield, AlertTriangle, Save, User, Clock, Calendar, Building2, CheckCircle, XCircle, Loader2, Phone, Pencil, Eye } from 'lucide-react';
import { toast } from 'sonner';

const CHECK_TYPES = [
  { key: 'nh_criminal', label: 'NH Criminal History Check' },
  { key: 'national_criminal', label: 'National Criminal Background Check' },
  { key: 'sex_offender', label: 'Sex Offender Registry Check' },
  { key: 'child_abuse', label: 'Child Abuse Registry Check' },
  { key: 'vulnerable_adult', label: 'Vulnerable Adult Registry Check' },
  { key: 'motor_vehicle', label: 'Motor Vehicle Record Check' },
  { key: 'reference_check', label: 'Reference Check' },
];

const CHECK_STATUSES = [
  { value: 'not_required', label: 'Not Required' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
];

const CLEARANCE_LEVELS = [
  { value: 'level_1', label: 'Level 1 - Community Volunteer' },
  { value: 'level_2', label: 'Level 2 - Youth Volunteer' },
  { value: 'level_3', label: 'Level 3 - Vulnerable Population' },
];

const CLEARANCE_STATUSES = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'verified', label: 'Verified' },
  { value: 'expired', label: 'Expired' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'revoked', label: 'Revoked' },
];

const LEVEL_LABELS = { level_1: 'General', level_2: 'Approved', level_3: 'Trusted' };

const ParticipantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [participant, setParticipant] = useState(null);
  const [passportData, setPassportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Passport form
  const [passportForm, setPassportForm] = useState({
    clearance_level: '',
    clearance_status: 'not_started',
    verification_date: '',
    expiration_date: '',
  });
  const [bgChecks, setBgChecks] = useState([]);

  // Org alert form
  const [alertForm, setAlertForm] = useState({ status: 'none', note: '' });

  // Profile edit form
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', date_of_birth: '' });
  const [profileEditMode, setProfileEditMode] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [detailRes, passportRes] = await Promise.all([
        axios.get(`${API}/admin/participants/${id}`),
        axios.get(`${API}/volunteer/${id}/passport-simple`),
      ]);
      setParticipant(detailRes.data);
      setPassportData(passportRes.data);

      // Init forms
      const p = detailRes.data;
      setPassportForm({
        clearance_level: p.clearance_level || '',
        clearance_status: p.clearance_status || 'not_started',
        verification_date: p.verification_date || '',
        expiration_date: p.expiration_date || '',
      });

      // Init bg checks
      const existing = p.background_checks || [];
      const checksMap = {};
      existing.forEach(c => { checksMap[c.check_type] = c; });
      setBgChecks(CHECK_TYPES.map(ct => ({
        check_type: ct.key,
        status: checksMap[ct.key]?.status || 'not_required',
        verification_date: checksMap[ct.key]?.verification_date || '',
        expiration_date: checksMap[ct.key]?.expiration_date || '',
        verified_by_org_name: checksMap[ct.key]?.verified_by_org_name || '',
      })));

      setAlertForm({
        status: p.org_alert_status || 'none',
        note: p.org_alert_note || '',
      });

      setProfileForm({
        name: p.name || '',
        phone: p.phone || '',
        date_of_birth: p.date_of_birth || '',
      });
    } catch (error) {
      toast.error('Failed to load participant');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSavePassport = async () => {
    setSaving(true);
    try {
      const checksToSave = bgChecks.filter(c => c.status !== 'not_required').map(c => ({
        check_type: c.check_type,
        status: c.status,
        verification_date: c.verification_date || null,
        expiration_date: c.expiration_date || null,
      }));

      await axios.put(`${API}/admin/participants/${id}/passport`, {
        clearance_level: passportForm.clearance_level || null,
        clearance_status: passportForm.clearance_status,
        verification_date: passportForm.verification_date || null,
        expiration_date: passportForm.expiration_date || null,
        background_checks: checksToSave,
      });
      toast.success('Passport updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAlert = async () => {
    try {
      await axios.put(`${API}/admin/participants/${id}/org-alert`, {
        org_alert_status: alertForm.status,
        org_alert_note: alertForm.note,
      });
      toast.success('Alert updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update alert');
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      const data = {};
      if (profileForm.name) data.name = profileForm.name;
      if (profileForm.phone !== undefined) data.phone = profileForm.phone;
      if (profileForm.date_of_birth !== undefined) data.date_of_birth = profileForm.date_of_birth;
      await axios.put(`${API}/admin/participants/${id}/profile`, data);
      toast.success('Profile updated');
      setProfileEditMode(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const updateBgCheck = (idx, field, value) => {
    setBgChecks(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  const canEdit = user?.role === 'admin' || user?.role === 'partner_admin';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF4EA]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-lg text-[#888]">Loading...</div>
        </div>
      </div>
    );
  }

  if (!participant) {
    return (
      <div className="min-h-screen bg-[#FBF4EA]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <p className="text-[#888]">Participant not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="participant-detail-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#8B2E3C] font-medium mb-4 hover:underline"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#222]">{participant.name}</h1>
            <p className="text-sm text-[#666]">{participant.email}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate(`/admin/participants/${id}/view-as`)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#8B2E3C] text-[#8B2E3C] hover:bg-[#FBF4EA] text-sm font-medium"
                data-testid="view-as-participant-button"
                title="See what this participant sees (read-only)"
              >
                <Eye className="w-4 h-4" /> View as Participant
              </button>
            )}
            <div className="bg-white px-4 py-2 rounded-lg border-2 border-[#E9DDCF]">
              <div className="text-xs text-[#888]">Approved Hours</div>
              <div className="text-xl font-bold text-[#8B2E3C]">{participant.completed_hours.toFixed(1)}h</div>
            </div>
            <div className="bg-white px-4 py-2 rounded-lg border-2 border-[#E9DDCF]">
              <div className="text-xs text-[#888]">Level</div>
              <div className="text-sm font-semibold text-[#222]">{LEVEL_LABELS[participant.volunteer_level] || 'General'}</div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left Column: Passport Card (2/5) */}
          <div className="lg:col-span-2">
            <SimplePassportCard data={passportData} />
          </div>

          {/* Right Column: Management (3/5) */}
          <div className="lg:col-span-3 space-y-6">
            {/* === PERSONAL INFORMATION === */}
            <div className="card" data-testid="profile-info-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[#222] flex items-center gap-2">
                  <User className="w-5 h-5 text-[#8B2E3C]" />
                  Personal Information
                </h2>
                {canEdit && !profileEditMode && (
                  <button onClick={() => setProfileEditMode(true)} className="text-sm text-[#8B2E3C] hover:underline flex items-center gap-1" data-testid="edit-profile-btn">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">Full Name</label>
                  <input
                    type="text"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm(f => ({ ...f, name: e.target.value }))}
                    className={`input-field ${!profileEditMode ? 'bg-gray-50' : ''}`}
                    readOnly={!profileEditMode}
                    data-testid="admin-profile-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">Email</label>
                  <input
                    type="email"
                    value={participant.email}
                    className="input-field bg-gray-100"
                    disabled
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Phone
                  </label>
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className={`input-field ${!profileEditMode ? 'bg-gray-50' : ''}`}
                    readOnly={!profileEditMode}
                    data-testid="admin-profile-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Date of Birth
                  </label>
                  <input
                    type="date"
                    value={profileForm.date_of_birth}
                    onChange={(e) => setProfileForm(f => ({ ...f, date_of_birth: e.target.value }))}
                    className={`input-field ${!profileEditMode ? 'bg-gray-50' : ''}`}
                    readOnly={!profileEditMode}
                    data-testid="admin-profile-dob"
                  />
                </div>
              </div>

              {profileEditMode && (
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handleSaveProfile}
                    disabled={profileSaving}
                    className="btn-primary flex items-center gap-2"
                    data-testid="admin-save-profile-btn"
                  >
                    {profileSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Profile</>}
                  </button>
                  <button onClick={() => { setProfileEditMode(false); setProfileForm({ name: participant.name || '', phone: participant.phone || '', date_of_birth: participant.date_of_birth || '' }); }} className="btn-outline">Cancel</button>
                </div>
              )}
            </div>

            {/* === CLEARANCE / PASSPORT MANAGEMENT === */}
            <div className="card" data-testid="passport-management-section">
              <h2 className="text-lg font-bold text-[#222] mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#8B2E3C]" />
                Volunteer Passport Management
              </h2>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">Clearance Level</label>
                  <select
                    value={passportForm.clearance_level}
                    onChange={(e) => setPassportForm(f => ({ ...f, clearance_level: e.target.value }))}
                    className="input-field"
                    disabled={!canEdit}
                    data-testid="clearance-level-select"
                  >
                    <option value="">Not Assigned</option>
                    {CLEARANCE_LEVELS.map(l => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">Clearance Status</label>
                  <select
                    value={passportForm.clearance_status}
                    onChange={(e) => setPassportForm(f => ({ ...f, clearance_status: e.target.value }))}
                    className="input-field"
                    disabled={!canEdit}
                    data-testid="clearance-status-select"
                  >
                    {CLEARANCE_STATUSES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">Verification Date</label>
                  <input
                    type="date"
                    value={passportForm.verification_date}
                    onChange={(e) => setPassportForm(f => ({ ...f, verification_date: e.target.value }))}
                    className="input-field"
                    disabled={!canEdit}
                    data-testid="verification-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">Expiration Date</label>
                  <input
                    type="date"
                    value={passportForm.expiration_date}
                    onChange={(e) => setPassportForm(f => ({ ...f, expiration_date: e.target.value }))}
                    className="input-field"
                    disabled={!canEdit}
                    data-testid="expiration-date-input"
                  />
                </div>
              </div>

              {/* Background Check Verification Types */}
              <h3 className="text-sm font-bold text-[#222] mb-3 mt-5">Background Check Verification Types</h3>
              <div className="space-y-3">
                {bgChecks.map((check, idx) => {
                  const label = CHECK_TYPES.find(ct => ct.key === check.check_type)?.label || check.check_type;
                  return (
                    <div key={check.check_type} className="bg-[#FBF4EA] rounded-lg p-3" data-testid={`bg-check-${check.check_type}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-[#222] min-w-[200px]">{label}</span>
                        <select
                          value={check.status}
                          onChange={(e) => updateBgCheck(idx, 'status', e.target.value)}
                          className="input-field text-sm py-1.5 flex-1"
                          disabled={!canEdit}
                          data-testid={`bg-check-status-${check.check_type}`}
                        >
                          {CHECK_STATUSES.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                      {check.status !== 'not_required' && (
                        <div className="flex gap-2 ml-0 sm:ml-[208px]">
                          <div className="flex-1">
                            <label className="block text-xs text-[#888]">Verified</label>
                            <input
                              type="date"
                              value={check.verification_date}
                              onChange={(e) => updateBgCheck(idx, 'verification_date', e.target.value)}
                              className="input-field text-sm py-1"
                              disabled={!canEdit}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-[#888]">Expires</label>
                            <input
                              type="date"
                              value={check.expiration_date}
                              onChange={(e) => updateBgCheck(idx, 'expiration_date', e.target.value)}
                              className="input-field text-sm py-1"
                              disabled={!canEdit}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {canEdit && (
                <button
                  onClick={handleSavePassport}
                  disabled={saving}
                  className="btn-primary mt-4 w-full sm:w-auto flex items-center justify-center gap-2"
                  data-testid="save-passport-button"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Passport'}
                </button>
              )}
            </div>

            {/* === ORG ALERT === */}
            <div className="card" data-testid="org-alert-section">
              <h2 className="text-lg font-bold text-[#222] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Organizational Alert
              </h2>

              {participant.org_alert_status === 'active' && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 mb-4" data-testid="active-alert-display">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold text-red-700 text-sm">Active Organizational Alert</div>
                      <div className="text-xs text-red-600 mt-1">
                        This participant has an active organizational alert. Please contact the reporting organization before assigning service.
                      </div>
                      {participant.org_alert_org_name && (
                        <div className="text-xs text-red-500 mt-1">Placed by: {participant.org_alert_org_name}</div>
                      )}
                      {participant.org_alert_note && (
                        <div className="text-xs text-red-500 mt-0.5">Note: {participant.org_alert_note}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {canEdit && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-[#444] mb-1">Alert Status</label>
                    <select
                      value={alertForm.status}
                      onChange={(e) => setAlertForm(f => ({ ...f, status: e.target.value }))}
                      className="input-field"
                      data-testid="alert-status-select"
                    >
                      <option value="none">None</option>
                      <option value="active">Active</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>
                  {alertForm.status === 'active' && (
                    <div>
                      <label className="block text-sm font-medium text-[#444] mb-1">Alert Note (visible to other orgs)</label>
                      <textarea
                        value={alertForm.note}
                        onChange={(e) => setAlertForm(f => ({ ...f, note: e.target.value }))}
                        className="input-field min-h-[80px]"
                        placeholder="Brief note for other organizations..."
                        data-testid="alert-note-input"
                      />
                    </div>
                  )}
                  <button
                    onClick={handleSaveAlert}
                    className="btn-primary flex items-center gap-2"
                    data-testid="save-alert-button"
                  >
                    <Save className="w-4 h-4" /> Save Alert
                  </button>
                </div>
              )}
            </div>

            {/* === DISCLAIMER === */}
            <div className="text-xs text-[#999] italic leading-relaxed px-1" data-testid="passport-full-disclaimer">
              Volunteer passport and clearance information is assigned and maintained by participating organizations. This platform does not store criminal history reports or detailed background check documents. Organizational alerts are provided only to prompt follow-up communication and should not be used as the sole basis for a placement decision.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantDetail;
